import type { Language } from "@/contexts/LanguageContext";

/** Phase 1 home content: dual-product positioning (FONDEA + SOLVENN). */
export const FONDEA_HREF = "/fondea";
export const FONDEA_WAITLIST_HREF = "/lanzamiento-fondea";
export const WHITE_LABEL_HREF = "/white-label";
export const CASE_STUDIES_HREF = "/resources/case-studies";

interface Titled {
  title: string;
  body: string;
}

export interface HomeV2Content {
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    soon: string;
    fondea: { tag: string; slogan: string; body: string; primary: string; secondary: string };
    whiteLabel: { tag: string; title: string; body: string; cta: string };
  };
  needs: {
    title: string;
    fondea: { tag: string; title: string; body: string; items: string[]; cta: string };
    whiteLabel: { tag: string; title: string; body: string; items: string[]; cta: string };
  };
  fondeaFlow: {
    eyebrow: string;
    title: string;
    subtitle: string;
    chain: string[];
    cycle: string[];
    slogan: string;
    sloganSub: string;
  };
  fondeaFeatures: { eyebrow: string; title: string; items: Titled[] };
  fondeaSteps: { eyebrow: string; title: string; steps: Titled[]; cta: string };
  funders: { eyebrow: string; title: string; body: string; items: string[]; cta: string };
  divider: { kicker: string; title: string; titleAccent: string; cta: string };
  whiteLabel: {
    eyebrow: string;
    title: string[];
    body: string;
    body2: string;
    sources: string[];
    layer: string;
    products: string[];
  };
  wlProblems: { title: string; items: Titled[] };
  wlProducts: { title: string; items: Titled[]; note: string };
  wlIntegration: { eyebrow: string; title: string; body: string; systems: string[]; items: Titled[] };
  cases: { eyebrow: string; title: string; items: { tag: string; body: string }[]; cta: string };
  maturity: {
    eyebrow: string;
    title: string;
    yes: string;
    no: string;
    progress: string;
    questions: string[];
    results: { high: Titled; mid: Titled; low: Titled };
    score: string;
    cta: string;
    reset: string;
  };
  method: { eyebrow: string; title: string; steps: Titled[] };
  why: { eyebrow: string; title: string; lead: string; items: Titled[] };
  finalCta: {
    title: string;
    fondea: { body: string; cta: string };
    whiteLabel: { body: string; cta: string };
  };
}

const es: HomeV2Content = {
  hero: {
    eyebrow: "IBYLIT · Infraestructura financiera para la nueva economía empresarial",
    title: "Conectamos empresas, instituciones financieras y capital.",
    subtitle:
      "Desarrollamos infraestructura financiera que integra facturación, pagos, operaciones comerciales y fuentes de financiamiento en un mismo ecosistema.",
    soon: "Próximo lanzamiento",
    fondea: {
      tag: "Para empresas y PYMES",
      slogan: "Emita. Reciba. Cobre. Pague. Financie.",
      body: "FONDEA abre a las empresas nuevas formas de financiar su operación.",
      primary: "Conocer FONDEA",
      secondary: "Unirme a la lista de espera",
    },
    whiteLabel: {
      tag: "PLATAFORMA WHITE LABEL PARA INSTITUCIONES FINANCIERAS",
      title: "Modernice productos financieros sin reemplazar su core.",
      body: "SOLVENN permite a bancos y entidades financieras lanzar y modernizar productos de factoring, confirming y pagos B2B bajo su propia marca.",
      cta: "Conocer SOLVENN",
    },
  },
  needs: {
    title: "¿Dónde está hoy su necesidad?",
    fondea: {
      tag: "Para empresas y PYMES",
      title: "Liquidez para seguir creciendo.",
      body: "Vender no siempre significa cobrar inmediatamente. FONDEA conecta la operación diaria de la empresa con herramientas para emitir, recibir, cobrar, pagar y buscar alternativas de financiamiento dentro de un mismo ecosistema.",
      items: [
        "Tengo facturas por cobrar.",
        "Necesito liquidez antes del vencimiento.",
        "Necesito pagar proveedores.",
        "Quiero organizar mis cuentas por cobrar.",
        "Quiero encontrar alternativas de financiamiento.",
        "Necesito visualizar mejor mi flujo operativo.",
        "Quiero centralizar documentos y operaciones.",
      ],
      cta: "Descubrir FONDEA",
    },
    whiteLabel: {
      tag: "Para instituciones financieras",
      title: "Modernice su infraestructura financiera.",
      body: "SOLVENN permite lanzar o modernizar productos financieros digitales sin sustituir el core existente, con una plataforma white label.",
      items: [
        "Procesos fragmentados.",
        "Operaciones manuales.",
        "Integraciones difíciles de escalar.",
        "Productos que tardan demasiado en salir al mercado.",
        "Falta de trazabilidad.",
        "Sistemas legacy.",
        "Necesidad de conectar core, ERP y terceros.",
        "Necesidad de lanzar nuevos productos financieros.",
      ],
      cta: "Explorar SOLVENN",
    },
  },
  fondeaFlow: {
    eyebrow: "FONDEA",
    title: "Todo empieza con una operación.",
    subtitle:
      "FONDEA convierte facturas, pagos y relaciones comerciales en oportunidades para administrar mejor la liquidez de una empresa.",
    chain: ["Empresa", "Factura", "Cuenta por cobrar", "FONDEA", "Alternativas de financiamiento"],
    cycle: ["Facturación", "Cobro", "Pago", "Financiamiento", "Seguimiento"],
    slogan: "Emita. Reciba. Cobre. Pague. Financie.",
    sloganSub: "Todo dentro de un mismo ecosistema.",
  },
  fondeaFeatures: {
    eyebrow: "Capacidades de FONDEA",
    title: "Una operación financiera completa, en un solo lugar.",
    items: [
      { title: "Facturación y documentos", body: "Centralice los documentos que originan la operación." },
      { title: "Cuentas por cobrar", body: "Visualice facturas pendientes y oportunidades de liquidez." },
      { title: "Factoring", body: "Obtenga alternativas para anticipar cuentas por cobrar." },
      { title: "Confirming", body: "Conecte pagos a proveedores con alternativas de financiamiento." },
      { title: "Pagos B2B", body: "Organice pagos empresariales y su trazabilidad." },
      { title: "Marketplace de financiamiento", body: "Conecte necesidades de liquidez con entidades dispuestas a financiarlas." },
      { title: "Subastas", body: "Permita que diferentes fondeadores compitan por una operación cuando corresponda." },
      { title: "Trazabilidad", body: "Mantenga evidencia del ciclo completo de cada operación." },
      { title: "Analítica", body: "Visualice información operativa y financiera relevante." },
    ],
  },
  fondeaSteps: {
    eyebrow: "Cómo funciona",
    title: "Cinco pasos, de la operación a la liquidez.",
    steps: [
      { title: "Opere", body: "Emita, reciba o cargue sus documentos." },
      { title: "Centralice", body: "FONDEA estructura la información de su operación." },
      { title: "Identifique oportunidades", body: "Detecte cuentas por cobrar o necesidades de financiamiento." },
      { title: "Conecte", body: "Acceda a alternativas disponibles según la operación." },
      { title: "Gestione", body: "Dé seguimiento al financiamiento, pagos y vencimientos desde una misma plataforma." },
    ],
    cta: "Quiero conocer FONDEA",
  },
  funders: {
    eyebrow: "FONDEA para fondeadores",
    title: "Más originación. Una infraestructura conectada.",
    body: "FONDEA permite conectar empresas que requieren liquidez con organizaciones que buscan originar operaciones financieras.",
    items: [
      "Acceso a oportunidades",
      "Información estructurada",
      "Reglas de elegibilidad",
      "Procesos digitales",
      "Integración",
      "Seguimiento de operaciones",
      "Trazabilidad",
    ],
    cta: "Quiero participar como fondeador",
  },
  divider: {
    kicker: "¿Ya tiene clientes, procesos e infraestructura financiera?",
    title: "Entonces no necesita otra plataforma.",
    titleAccent: "Necesita modernizar la que ya tiene.",
    cta: "Conocer SOLVENN",
  },
  whiteLabel: {
    eyebrow: "SOLVENN",
    title: ["Su producto.", "Su marca.", "Sus reglas.", "Nuestra infraestructura."],
    body: "SOLVENN permite a bancos, entidades financieras, cooperativas, bolsas y corporativos lanzar o modernizar productos financieros digitales sobre su infraestructura existente.",
    body2:
      "No sustituimos innecesariamente el core. Creamos una capa tecnológica que conecta procesos, participantes, documentos y datos dentro de un flujo controlado.",
    sources: ["Core", "ERP", "Identidad", "Pagos", "Sistemas fiscales"],
    layer: "SOLVENN FINANCIAL INFRASTRUCTURE LAYER",
    products: ["Factoring", "Confirming", "Pagos B2B", "Subastas", "Mercados de facturas"],
  },
  wlProblems: {
    title: "La modernización se frena cuando cada pieza opera por separado.",
    items: [
      { title: "Procesos fragmentados", body: "Aprobaciones, documentos y conciliaciones distribuidos entre diferentes sistemas y equipos." },
      { title: "Integraciones puntuales", body: "Conexiones difíciles de ampliar cuando cambia el producto, canal o país." },
      { title: "Lanzamientos lentos", body: "Desarrollar cada producto desde cero aumenta costos y tiempo de salida." },
      { title: "Trazabilidad incompleta", body: "La información llega tarde o sin una vista única de cada operación." },
    ],
  },
  wlProducts: {
    title: "Una infraestructura. Múltiples productos financieros.",
    items: [
      { title: "Factoring", body: "Liquidez sobre cuentas por cobrar con o sin recurso." },
      { title: "Confirming", body: "Financiamiento de proveedores y cadenas de suministro." },
      { title: "Pagos B2B", body: "Ejecución, comprobantes y conciliación de pagos empresariales." },
      { title: "Subastas", body: "Competencia entre fondeadores y asignación de condiciones." },
      { title: "Anotación", body: "Registro digital y trazabilidad del ciclo de vida del instrumento." },
      { title: "Custodia operativa", body: "Control de titularidad, eventos y cierre de las operaciones." },
    ],
    note: "Cada módulo puede funcionar individualmente o como parte de un flujo integrado.",
  },
  wlIntegration: {
    eyebrow: "Capa de integración",
    title: "La capa de integración preserva los sistemas existentes.",
    body: "SOLVENN conecta el producto financiero con el core, los ERPs, pagos, identidad, firma y sistemas fiscales.",
    systems: ["Core", "ERP", "Pagos", "Identidad", "Firma", "Fiscal"],
    items: [
      { title: "Cloud native y API first", body: "APIs REST y webhooks para integraciones desacopladas." },
      { title: "Orquestación de flujos", body: "Reglas, roles y estados coordinados de extremo a extremo." },
      { title: "Configuración e implementación", body: "Ambientes, controles y procesos preparados para producción." },
      { title: "Servicios administrados", body: "Monitoreo, continuidad, soporte y evolución posterior al lanzamiento." },
    ],
  },
  cases: {
    eyebrow: "Casos de éxito",
    title: "Infraestructura operando en mercados reales.",
    items: [
      { tag: "Banco · Costa Rica", body: "Confirming integrado con infraestructura bancaria, transferencias locales e interoperabilidad." },
      { tag: "Banco · Costa Rica", body: "Pagos corporativos y financiamiento de proveedores dentro de un flujo unificado." },
      { tag: "Plataforma · Argentina", body: "Anotación, custodia, negociación y liquidación de instrumentos." },
    ],
    cta: "Ver casos de éxito",
  },
  maturity: {
    eyebrow: "Autodiagnóstico",
    title: "¿Qué tan preparada está su operación para escalar?",
    yes: "Sí",
    no: "No",
    progress: "respondidas",
    questions: [
      "¿Existe una vista única del ciclo de cada operación?",
      "¿Las facturas ingresan directamente desde ERP o interfaces?",
      "¿Las reglas de elegibilidad se aplican automáticamente?",
      "¿Los participantes operan con roles y permisos definidos?",
      "¿Puede lanzar nuevos productos sin modificar el core?",
      "¿La conciliación y los comprobantes se generan dentro del flujo?",
      "¿Los eventos mantienen evidencia y trazabilidad?",
      "¿Puede incorporar nuevos fondeadores fácilmente?",
      "¿Los ambientes y controles están preparados para escalar?",
      "¿Existe una estrategia de evolución y soporte continuo?",
    ],
    results: {
      high: { title: "Alta madurez", body: "Operación preparada para expansión." },
      mid: { title: "Madurez media", body: "Existen oportunidades claras de integración y automatización." },
      low: { title: "Modernización prioritaria", body: "Su operación presenta múltiples oportunidades de transformación." },
    },
    score: "respuestas afirmativas",
    cta: "Solicitar evaluación",
    reset: "Reiniciar",
  },
  why: {
    eyebrow: "¿Por qué IBYLIT?",
    title: "Nuestra ventaja está en lo que conectamos.",
    lead: "No desarrollamos soluciones financieras aisladas. Construimos infraestructura tecnológica que integra empresas, instituciones financieras y fuentes de capital, simplificando operaciones complejas y habilitando nuevas oportunidades de negocio.",
    items: [
      { title: "Integración profunda", body: "Conexión con sistemas bancarios, ERPs, pasarelas de pago y sistemas fiscales, aprovechando la infraestructura existente." },
      { title: "Experiencia financiera especializada", body: "Conocimiento operativo en factoring, confirming, pagos B2B, cuentas por cobrar y mercados de facturas." },
      { title: "Un ecosistema, múltiples oportunidades", body: "FONDEA para la liquidez de las empresas; SOLVENN para la modernización de instituciones financieras con infraestructura white label." },
      { title: "Tecnología diseñada para Latinoamérica", body: "Arquitectura modular adaptable a distintas regulaciones, calendarios y modelos de negocio regionales." },
    ],
  },
  method: {
    eyebrow: "Metodología IBYLIT",
    title: "Del diagnóstico a la evolución continua.",
    steps: [
      { title: "Diagnóstico", body: "Procesos, sistemas y objetivos." },
      { title: "Diseño", body: "Modelo funcional y arquitectura." },
      { title: "Configuración", body: "Reglas, roles y ambientes." },
      { title: "Integración", body: "Core, ERP, pagos y terceros." },
      { title: "Operación", body: "Producción, monitoreo y control." },
      { title: "Evolución", body: "Nuevos módulos, productos y mercados." },
    ],
  },
  finalCta: {
    title: "¿Qué necesita resolver?",
    fondea: { body: "Quiero mejorar la liquidez de mi empresa.", cta: "Conocer FONDEA" },
    whiteLabel: { body: "Quiero modernizar o lanzar productos financieros.", cta: "Hablar con un especialista" },
  },
};

const en: HomeV2Content = {
  hero: {
    eyebrow: "IBYLIT · Financial infrastructure for the new business economy",
    title: "We connect businesses, financial institutions and capital.",
    subtitle:
      "We build financial infrastructure that integrates invoicing, payments, commercial operations and funding sources in a single ecosystem.",
    soon: "Coming soon",
    fondea: {
      tag: "For businesses and SMEs",
      slogan: "Issue. Receive. Collect. Pay. Finance.",
      body: "FONDEA opens new ways for businesses to finance their operations.",
      primary: "Discover FONDEA",
      secondary: "Join the waitlist",
    },
    whiteLabel: {
      tag: "For financial institutions",
      title: "Modernize financial products without replacing your core.",
      body: "SOLVENN enables banks and financial institutions to launch and modernize factoring, confirming and B2B payment products under their own brand.",
      cta: "Discover SOLVENN",
    },
  },
  needs: {
    title: "Where is your need today?",
    fondea: {
      tag: "For businesses and SMEs",
      title: "Liquidity to keep growing.",
      body: "Selling doesn't always mean getting paid right away. FONDEA connects a company's daily operations with tools to issue, receive, collect, pay and find financing alternatives in a single ecosystem.",
      items: [
        "I have invoices to collect.",
        "I need liquidity before maturity.",
        "I need to pay suppliers.",
        "I want to organize my receivables.",
        "I want to find financing alternatives.",
        "I need better visibility of my operating flow.",
        "I want to centralize documents and operations.",
      ],
      cta: "Discover FONDEA",
    },
    whiteLabel: {
      tag: "For financial institutions",
      title: "Modernize your financial infrastructure.",
      body: "SOLVENN lets you launch or modernize digital financial products without replacing your existing core.",
      items: [
        "Fragmented processes.",
        "Manual operations.",
        "Integrations that are hard to scale.",
        "Products that take too long to reach the market.",
        "Lack of traceability.",
        "Legacy systems.",
        "Need to connect core, ERP and third parties.",
        "Need to launch new financial products.",
      ],
      cta: "Explore SOLVENN",
    },
  },
  fondeaFlow: {
    eyebrow: "FONDEA",
    title: "Everything starts with a transaction.",
    subtitle:
      "FONDEA turns invoices, payments and commercial relationships into opportunities to better manage a company's liquidity.",
    chain: ["Company", "Invoice", "Receivable", "FONDEA", "Financing alternatives"],
    cycle: ["Invoicing", "Collection", "Payment", "Financing", "Tracking"],
    slogan: "Issue. Receive. Collect. Pay. Finance.",
    sloganSub: "All within a single ecosystem.",
  },
  fondeaFeatures: {
    eyebrow: "FONDEA capabilities",
    title: "A complete financial operation, in one place.",
    items: [
      { title: "Invoicing and documents", body: "Centralize the documents that originate each transaction." },
      { title: "Accounts receivable", body: "See pending invoices and liquidity opportunities." },
      { title: "Factoring", body: "Get alternatives to advance your receivables." },
      { title: "Confirming", body: "Connect supplier payments with financing alternatives." },
      { title: "B2B payments", body: "Organize business payments and their traceability." },
      { title: "Financing marketplace", body: "Match liquidity needs with institutions willing to fund them." },
      { title: "Auctions", body: "Let different funders compete for a transaction when appropriate." },
      { title: "Traceability", body: "Keep evidence of the full cycle of every transaction." },
      { title: "Analytics", body: "See relevant operational and financial information." },
    ],
  },
  fondeaSteps: {
    eyebrow: "How it works",
    title: "Five steps, from operation to liquidity.",
    steps: [
      { title: "Operate", body: "Issue, receive or upload your documents." },
      { title: "Centralize", body: "FONDEA structures your operational information." },
      { title: "Spot opportunities", body: "Detect receivables or financing needs." },
      { title: "Connect", body: "Access available alternatives based on the transaction." },
      { title: "Manage", body: "Track financing, payments and maturities from one platform." },
    ],
    cta: "I want to know FONDEA",
  },
  funders: {
    eyebrow: "FONDEA for funders",
    title: "More origination. One connected infrastructure.",
    body: "FONDEA connects businesses that need liquidity with organizations looking to originate financial transactions.",
    items: [
      "Access to opportunities",
      "Structured information",
      "Eligibility rules",
      "Digital processes",
      "Integration",
      "Transaction tracking",
      "Traceability",
    ],
    cta: "I want to join as a funder",
  },
  divider: {
    kicker: "Already have clients, processes and financial infrastructure?",
    title: "Then you don't need another platform.",
    titleAccent: "You need to modernize the one you have.",
    cta: "Discover SOLVENN",
  },
  whiteLabel: {
    eyebrow: "SOLVENN",
    title: ["Your product.", "Your brand.", "Your rules.", "Our infrastructure."],
    body: "SOLVENN lets banks, financial institutions, cooperatives, exchanges and corporates launch or modernize digital financial products on top of their existing infrastructure.",
    body2:
      "We don't replace your core unnecessarily. We build a technology layer that connects processes, participants, documents and data within a controlled flow.",
    sources: ["Core", "ERP", "Identity", "Payments", "Tax systems"],
    layer: "IBYLIT Financial Infrastructure Layer",
    products: ["Factoring", "Confirming", "B2B payments", "Auctions", "Invoice markets"],
  },
  wlProblems: {
    title: "Modernization stalls when every piece operates on its own.",
    items: [
      { title: "Fragmented processes", body: "Approvals, documents and reconciliations spread across systems and teams." },
      { title: "One-off integrations", body: "Connections that are hard to extend when the product, channel or country changes." },
      { title: "Slow launches", body: "Building each product from scratch increases cost and time to market." },
      { title: "Incomplete traceability", body: "Information arrives late or without a single view of each transaction." },
    ],
  },
  wlProducts: {
    title: "One infrastructure. Multiple financial products.",
    items: [
      { title: "Factoring", body: "Liquidity on receivables, with or without recourse." },
      { title: "Confirming", body: "Supplier and supply chain financing." },
      { title: "B2B payments", body: "Execution, receipts and reconciliation of business payments." },
      { title: "Auctions", body: "Competition among funders and allocation of terms." },
      { title: "Annotation", body: "Digital registry and lifecycle traceability of each instrument." },
      { title: "Operational custody", body: "Control of ownership, events and transaction closing." },
    ],
    note: "Each module can run on its own or as part of an integrated flow.",
  },
  wlIntegration: {
    eyebrow: "Integration layer",
    title: "The integration layer preserves your existing systems.",
    body: "SOLVENN connects the financial product with the core, ERPs, payments, identity, e-signature and tax systems.",
    systems: ["Core", "ERP", "Payments", "Identity", "E-signature", "Tax"],
    items: [
      { title: "Cloud native and API first", body: "REST APIs and webhooks for decoupled integrations." },
      { title: "Workflow orchestration", body: "Rules, roles and states coordinated end to end." },
      { title: "Configuration and implementation", body: "Environments, controls and processes ready for production." },
      { title: "Managed services", body: "Monitoring, continuity, support and post-launch evolution." },
    ],
  },
  cases: {
    eyebrow: "Case studies",
    title: "Infrastructure running in real markets.",
    items: [
      { tag: "Bank · Costa Rica", body: "Confirming integrated with banking infrastructure, local transfers and interoperability." },
      { tag: "Bank · Costa Rica", body: "Corporate payments and supplier financing within a unified flow." },
      { tag: "Platform · Argentina", body: "Annotation, custody, trading and settlement of instruments." },
    ],
    cta: "See case studies",
  },
  maturity: {
    eyebrow: "Self-assessment",
    title: "How ready is your operation to scale?",
    yes: "Yes",
    no: "No",
    progress: "answered",
    questions: [
      "Is there a single view of each transaction's lifecycle?",
      "Do invoices come in directly from ERP or interfaces?",
      "Are eligibility rules applied automatically?",
      "Do participants operate with defined roles and permissions?",
      "Can you launch new products without changing the core?",
      "Are reconciliation and receipts generated within the flow?",
      "Do events keep evidence and traceability?",
      "Can you onboard new funders easily?",
      "Are environments and controls ready to scale?",
      "Is there a strategy for ongoing evolution and support?",
    ],
    results: {
      high: { title: "High maturity", body: "Operation ready for expansion." },
      mid: { title: "Medium maturity", body: "There are clear integration and automation opportunities." },
      low: { title: "Modernization priority", body: "Your operation shows multiple transformation opportunities." },
    },
    score: "yes answers",
    cta: "Request an assessment",
    reset: "Restart",
  },
  why: {
    eyebrow: "Why IBYLIT?",
    title: "Our advantage lies in what we connect.",
    lead: "We don't build isolated financial solutions. We build technology infrastructure that integrates businesses, financial institutions and capital sources, simplifying complex operations and enabling new business opportunities.",
    items: [
      { title: "Deep integration", body: "Connections to banking systems, ERPs, payment gateways and tax systems, leveraging existing infrastructure." },
      { title: "Specialized financial expertise", body: "Operational know-how in factoring, confirming, B2B payments, receivables and invoice marketplaces." },
      { title: "One ecosystem, multiple opportunities", body: "FONDEA for business liquidity; SOLVENN for modernizing financial institutions with white-label infrastructure." },
      { title: "Technology built for Latin America", body: "Modular architecture adaptable to different regulations, calendars and regional business models." },
    ],
  },
  method: {
    eyebrow: "IBYLIT methodology",
    title: "From diagnosis to continuous evolution.",
    steps: [
      { title: "Diagnosis", body: "Processes, systems and goals." },
      { title: "Design", body: "Functional model and architecture." },
      { title: "Configuration", body: "Rules, roles and environments." },
      { title: "Integration", body: "Core, ERP, payments and third parties." },
      { title: "Operation", body: "Production, monitoring and control." },
      { title: "Evolution", body: "New modules, products and markets." },
    ],
  },
  finalCta: {
    title: "What do you need to solve?",
    fondea: { body: "I want to improve my company's liquidity.", cta: "Discover FONDEA" },
    whiteLabel: { body: "I want to modernize or launch financial products.", cta: "Talk to a specialist" },
  },
};

export const getHomeV2 = (language: Language): HomeV2Content => (language === "en" ? en : es);
